import Phaser from "phaser";
import { COLORS } from "../constants";
import { playImpactFlash } from "../ui/impactFlash";
import { PrimitiveBlueprint, PrimitivePart } from "./types";

import { HealthBar } from "../ui/HealthBar";
import { IntentIndicator, IntentType } from "../ui/IntentIndicator";

export type PrimitiveAvatarOptions = {
  width?: number;
  height?: number;
  scale?: number;
  selectionColor?: number;
  showHealthBar?: boolean;
  health?: { current: number; max: number };
  healthBarOptions?: Partial<import("../ui/HealthBar").HealthBarOptions>;
  nextAction?: {
    /** The type of intent (attack or defend) */
    intentType: IntentType;
    /** Damage/armor value to display */
    value: number;
    /** Whether this enemy is actively performing this action */
    isActive?: boolean;
  };
};

export class PrimitiveAvatar {
  private root: Phaser.GameObjects.Container;
  private selectionUnderline: Phaser.GameObjects.Rectangle;
  private intentIndicator?: IntentIndicator;
  private width: number;
  private frameHeight: number;
  private layout: ReturnType<typeof PrimitiveAvatar.computeLayout>;
  private healTween?: Phaser.Tweens.Tween;
  private healthBar?: HealthBar;
  private defendingBubble?: Phaser.GameObjects.Ellipse;

  constructor(
    private scene: Phaser.Scene,
    blueprint: PrimitiveBlueprint,
    options: PrimitiveAvatarOptions = {}
  ) {
    const baseW = blueprint.designSize.w;
    const baseH = blueprint.designSize.h;
    const targetW = options.width ?? (options.scale ? baseW * options.scale : baseW);
    const targetH = options.height ?? (options.scale ? baseH * options.scale : baseH);
    this.width = targetW;

    this.layout = PrimitiveAvatar.computeLayout(targetW, targetH, options);
    this.frameHeight = targetH + this.layout.topChrome + this.layout.bottomChrome;
    const selectionColor = this.toColor(options.selectionColor, 0xffffff);

    this.root = scene.add.container(0, 0);
    this.root.setSize(targetW, this.frameHeight);

    // Create intent indicator if nextAction is specified
    if (options.nextAction !== undefined) {
      this.intentIndicator = new IntentIndicator(scene, {
        intentType: options.nextAction.intentType,
        value: options.nextAction.value,
        isActive: options.nextAction.isActive,
      });
      // Position indicator below the health bar
      this.intentIndicator.setPosition(this.layout.indicatorX, this.layout.indicatorY);
      this.root.add(this.intentIndicator.getRoot());
    }

    const sortedParts = [...blueprint.parts].sort((a, b) => (a.z ?? 0) - (b.z ?? 0));
    sortedParts.forEach((part) => {
      const graphic = this.createPart(part, targetW, targetH);
      graphic.y += this.layout.topChrome;
      this.root.add(graphic);
    });

    if (options.showHealthBar && options.health) {
      this.healthBar = new HealthBar(scene, {
        width: this.layout.healthBarWidth,
        height: this.layout.healthBarHeight,
        ...options.healthBarOptions,
      });
      this.healthBar.setHP(options.health.current, options.health.max);
      this.healthBar.setPosition(this.layout.healthBarX, this.layout.healthBarY);
      this.root.add(this.healthBar.getRoot());
    }

    // Create selection highlight as banner/border around health bar
    this.selectionUnderline = scene.add.rectangle(
      this.layout.healthBarX + this.layout.healthBarWidth / 2,
      this.layout.healthBarY + this.layout.healthBarHeight / 2,
      this.layout.healthBarWidth + 8,
      this.layout.healthBarHeight + 8,
      selectionColor,
      0
    );
    this.selectionUnderline.setStrokeStyle(3, selectionColor, 0.8);
    this.selectionUnderline.setVisible(false);
    this.root.add(this.selectionUnderline);

    this.setNextAction(options.nextAction);
  }

  getRoot(): Phaser.GameObjects.Container {
    return this.root;
  }

  getWorldCenter(): { x: number; y: number } {
    const bounds = this.getWorldBounds();
    return { x: bounds.centerX, y: bounds.centerY };
  }

  getWorldBounds(): Phaser.Geom.Rectangle {
    const matrix = this.root.getWorldTransformMatrix();
    const topLeft = new Phaser.Math.Vector2();
    const bottomRight = new Phaser.Math.Vector2();
    matrix.transformPoint(0, 0, topLeft);
    matrix.transformPoint(this.width, this.frameHeight, bottomRight);
    return new Phaser.Geom.Rectangle(
      topLeft.x,
      topLeft.y,
      bottomRight.x - topLeft.x,
      bottomRight.y - topLeft.y
    );
  }

  setSelected(on: boolean) {
    this.selectionUnderline.setVisible(on);
    this.selectionUnderline.setAlpha(on ? 1 : 0.4);
    this.root.setAlpha(1);
  }

  setAlive(on: boolean) {
    this.root.setAlpha(on ? 1 : 0.45);
  }

  playHit() {
    playImpactFlash(this.scene, this.root);
  }

  playHeal() {
    const bounds = this.getWorldBounds();
    const healColor = Phaser.Display.Color.HexStringToColor(COLORS.healText).color;
    const overlay = this.scene.add.rectangle(
      bounds.centerX,
      bounds.centerY,
      bounds.width * 1.04,
      bounds.height * 1.04,
      healColor,
      0.22
    );
    overlay.setDepth((this.root.depth ?? 0) + 30);
    this.scene.tweens.add({
      targets: overlay,
      alpha: 0,
      duration: 200,
      ease: "Sine.easeOut",
      onComplete: () => overlay.destroy(),
    });

    const baseScaleX = (this.root.scaleX ?? 1) as number;
    const baseScaleY = (this.root.scaleY ?? 1) as number;
    this.healTween?.stop();
    this.root.setScale(baseScaleX, baseScaleY);

    this.healTween = this.scene.tweens.add({
      targets: this.root,
      scaleX: baseScaleX * 1.05,
      scaleY: baseScaleY * 1.05,
      duration: 180,
      ease: "Sine.easeOut",
      yoyo: true,
      onComplete: () => {
        this.root.setScale(baseScaleX, baseScaleY);
      },
    });
  }

  playDefend() {
    const bounds = this.getWorldBounds();
    const shieldColor = Phaser.Display.Color.HexStringToColor(COLORS.shieldText).color;

    // Create a shield-like arc overlay
    const overlay = this.scene.add.rectangle(
      bounds.centerX,
      bounds.centerY,
      bounds.width * 1.1,
      bounds.height * 1.1,
      shieldColor,
      0.28
    );
    overlay.setStrokeStyle(3, shieldColor, 0.7);
    overlay.setDepth((this.root.depth ?? 0) + 30);

    // Flash and fade the shield effect
    this.scene.tweens.add({
      targets: overlay,
      alpha: 0,
      scaleX: 1.15,
      scaleY: 1.15,
      duration: 350,
      ease: "Sine.easeOut",
      onComplete: () => overlay.destroy(),
    });
  }

  setPosition(x: number, y: number) {
    this.root.setPosition(x, y);
  }

  /** Show or hide persistent defending bubble around avatar */
  setDefending(on: boolean) {
    if (on) {
      if (!this.defendingBubble) {
        const shieldColor = Phaser.Display.Color.HexStringToColor(COLORS.shieldText).color;
        this.defendingBubble = this.scene.add.ellipse(
          this.width / 2,
          this.frameHeight / 2,
          this.width * 1.15,
          this.frameHeight * 1.1,
          shieldColor,
          0.18
        );
        this.defendingBubble.setStrokeStyle(2, shieldColor, 0.5);
        this.root.addAt(this.defendingBubble, 0); // Add behind other elements
      }
      this.defendingBubble.setVisible(true);
    } else {
      this.defendingBubble?.setVisible(false);
    }
  }

  destroy() {
    this.healTween?.stop();
    this.healthBar?.destroy();
    this.intentIndicator?.destroy();
    this.defendingBubble?.destroy();
    this.root.destroy(true);
  }

  setHealth(current: number, max: number) {
    if (this.healthBar) {
      this.healthBar.setHP(current, max);
    }
  }

  setNextAction(next?: PrimitiveAvatarOptions["nextAction"]) {
    // If no indicator exists yet and we need one, create it
    if (next && !this.intentIndicator) {
      this.intentIndicator = new IntentIndicator(this.scene, {
        intentType: next.intentType,
        value: next.value,
        isActive: next.isActive,
      });
      // Position indicator below the health bar
      this.intentIndicator.setPosition(this.layout.indicatorX, this.layout.indicatorY);
      this.root.add(this.intentIndicator.getRoot());
      this.intentIndicator.playEntranceAnimation();
    }

    if (!this.intentIndicator) {
      return;
    }

    if (!next) {
      this.intentIndicator.setVisible(false);
      return;
    }

    // Update the intent type and value
    this.intentIndicator.setIntent(next.intentType, next.value);
    this.intentIndicator.setActive(next.isActive ?? false);
    this.intentIndicator.setVisible(true);
  }

  getSize() {
    return { width: this.width, height: this.frameHeight };
  }

  static measure(
    blueprint: PrimitiveBlueprint,
    options: PrimitiveAvatarOptions = {}
  ): { width: number; height: number } {
    const baseW = blueprint.designSize.w;
    const baseH = blueprint.designSize.h;
    const targetW = options.width ?? (options.scale ? baseW * options.scale : baseW);
    const targetH = options.height ?? (options.scale ? baseH * options.scale : baseH);
    const layout = PrimitiveAvatar.computeLayout(targetW, targetH, options);
    return {
      width: targetW,
      height: targetH + layout.topChrome + layout.bottomChrome,
    };
  }

  private toColor(input: number | string | undefined, fallback: number) {
    if (typeof input === "number") return input;
    if (typeof input === "string") return Phaser.Display.Color.HexStringToColor(input).color;
    return fallback;
  }

  private static computeLayout(targetW: number, targetH: number, options: PrimitiveAvatarOptions) {
    const showHealth = Boolean(options.showHealthBar && options.health);
    const healthBarHeight = showHealth ? (options.healthBarOptions?.height ?? 18) : 0;
    const healthPadding = showHealth ? Math.max(8, targetW * 0.06) : 0;
    const healthGap = showHealth ? 6 : 0;
    const selectionHeight = 6;
    const indicatorHeight = options.nextAction ? 20 : 0; // Compact indicator height
    const indicatorGap = options.nextAction ? 4 : 0; // Gap below health bar

    const topChrome = 0;
    const bottomChrome = showHealth
      ? healthBarHeight +
        indicatorHeight +
        indicatorGap +
        healthPadding +
        healthGap +
        selectionHeight
      : selectionHeight + 6;

    const healthBarWidth = Math.max(64, targetW - healthPadding * 2);
    const healthBarX = (targetW - healthBarWidth) / 2;
    const healthBarY = targetH + topChrome + healthGap;
    const indicatorX = healthBarX; // Align with health bar left edge
    const indicatorY = healthBarY + healthBarHeight + indicatorGap; // Below health bar

    return {
      topChrome,
      bottomChrome,
      healthBarWidth,
      healthBarHeight,
      healthBarX,
      healthBarY,
      selectionHeight,
      indicatorX,
      indicatorY,
    } as const;
  }

  private createPart(
    part: PrimitivePart,
    width: number,
    height: number
  ): Phaser.GameObjects.Graphics {
    const graphics = this.scene.add.graphics();
    const bounds = {
      x: part.bounds.x * width,
      y: part.bounds.y * height,
      w: part.bounds.w * width,
      h: part.bounds.h * height,
    };

    if (part.style.fill !== undefined) {
      graphics.fillStyle(part.style.fill, part.style.fillAlpha ?? 1);
    }
    if (part.style.stroke !== undefined && part.style.strokeWidth !== undefined) {
      graphics.lineStyle(part.style.strokeWidth, part.style.stroke, part.style.strokeAlpha ?? 1);
    }

    switch (part.type) {
      case "rect":
        if (part.style.fill !== undefined) {
          graphics.fillRect(bounds.x, bounds.y, bounds.w, bounds.h);
        }
        if (part.style.stroke !== undefined && part.style.strokeWidth !== undefined) {
          graphics.strokeRect(bounds.x, bounds.y, bounds.w, bounds.h);
        }
        break;
      case "roundRect":
        if (part.style.fill !== undefined) {
          graphics.fillRoundedRect(
            bounds.x,
            bounds.y,
            bounds.w,
            bounds.h,
            Math.min(bounds.w, bounds.h) * 0.18
          );
        }
        if (part.style.stroke !== undefined && part.style.strokeWidth !== undefined) {
          graphics.strokeRoundedRect(
            bounds.x,
            bounds.y,
            bounds.w,
            bounds.h,
            Math.min(bounds.w, bounds.h) * 0.18
          );
        }
        break;
      case "ellipse":
        if (part.style.fill !== undefined) {
          graphics.fillEllipse(
            bounds.x + bounds.w / 2,
            bounds.y + bounds.h / 2,
            bounds.w,
            bounds.h
          );
        }
        if (part.style.stroke !== undefined && part.style.strokeWidth !== undefined) {
          graphics.strokeEllipse(
            bounds.x + bounds.w / 2,
            bounds.y + bounds.h / 2,
            bounds.w,
            bounds.h
          );
        }
        break;
      case "triangle": {
        // Define triangle vertices (pointing up by default)
        let x1 = bounds.x + bounds.w / 2; // Top center
        let y1 = bounds.y;
        let x2 = bounds.x; // Bottom left
        let y2 = bounds.y + bounds.h;
        let x3 = bounds.x + bounds.w; // Bottom right
        let y3 = bounds.y + bounds.h;

        // Apply rotation if specified (rotate around triangle's center)
        if (part.rotation !== undefined && part.rotation !== 0) {
          const centerX = bounds.x + bounds.w / 2;
          const centerY = bounds.y + bounds.h / 2;
          const cos = Math.cos(part.rotation);
          const sin = Math.sin(part.rotation);

          // Rotate each vertex around the center
          const rotate = (x: number, y: number) => {
            const dx = x - centerX;
            const dy = y - centerY;
            return {
              x: centerX + dx * cos - dy * sin,
              y: centerY + dx * sin + dy * cos,
            };
          };

          const p1 = rotate(x1, y1);
          const p2 = rotate(x2, y2);
          const p3 = rotate(x3, y3);
          x1 = p1.x;
          y1 = p1.y;
          x2 = p2.x;
          y2 = p2.y;
          x3 = p3.x;
          y3 = p3.y;
        }

        if (part.style.fill !== undefined) {
          graphics.fillTriangle(x1, y1, x2, y2, x3, y3);
        }
        if (part.style.stroke !== undefined && part.style.strokeWidth !== undefined) {
          graphics.strokeTriangle(x1, y1, x2, y2, x3, y3);
        }
        break;
      }
      case "line":
        graphics.lineStyle(
          part.style.strokeWidth ?? 2,
          part.style.stroke ?? 0xffffff,
          part.style.strokeAlpha ?? 1
        );
        graphics.beginPath();
        graphics.moveTo(bounds.x, bounds.y);
        graphics.lineTo(bounds.x + bounds.w, bounds.y + bounds.h);
        graphics.strokePath();
        break;
      case "circle": {
        const radius = Math.min(bounds.w, bounds.h) / 2;
        const cx = bounds.x + bounds.w / 2;
        const cy = bounds.y + bounds.h / 2;
        if (part.style.fill !== undefined) {
          graphics.fillCircle(cx, cy, radius);
        }
        if (part.style.stroke !== undefined && part.style.strokeWidth !== undefined) {
          graphics.strokeCircle(cx, cy, radius);
        }
        break;
      }
      default:
        break;
    }

    return graphics;
  }
}
